import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'src/utils/store'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { actions } from '../../DivergenceDemandsState'
import {
  selectActiveDivergenceId, selectDivergenceActiveAddress, selectDivergenceActiveProductDesc, selectProductResolutionData, selectActiveDivergenceItemId,
} from '../../DivergenceDemandsSelector'
import Page from '../../components/Page/Page'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import AddressStep from './components/AddressStep'
import ProductStep from './components/ProductStep'
import CountingStep from './components/CountingStep'
import FinishedStep from './components/FinishedStep'

interface AddressFormData {
  addressCode: string
}

interface ProductFormData {
  barcode: string
}

interface CountFormData {
  quantity: string
}

enum DivergenceFlowStep {
  ADDRESS = 'ADDRESS',
  PRODUCT = 'PRODUCT',
  COUNTING = 'COUNTING',
  FINISHED = 'FINISHED',
}

const DivergenceProductPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<DivergenceFlowStep>(DivergenceFlowStep.ADDRESS)
  const [hasAddressError, setHasAddressError] = useState(false)
  const [isProductRevalidation, setIsProductRevalidation] = useState(false)
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)

  const divergenceActiveAddress = useSelector(selectDivergenceActiveAddress)
  const divergenceActiveProductDesc = useSelector(selectDivergenceActiveProductDesc)
  const productResolutionData = useSelector(selectProductResolutionData)
  const activeDivergenceItemId = useSelector(selectActiveDivergenceItemId)
  const user = useSelector((state: any) => state.divergenceDemands.user)

  const {
    register: registerAddress,
    getValues: getAddressValues,
    setValue: setAddressValue,
  } = useForm<AddressFormData>()
  const {
    register: registerProduct,
    getValues: getProductValues,
    setValue: setProductValue,
  } = useForm<ProductFormData>()
  const {
    register: registerCount,
    handleSubmit: handleCountSubmit,
    setValue: setCountValue,
    formState: { errors: countErrors },
  } = useForm<CountFormData>()

  const activeDivergenceId = useSelector(selectActiveDivergenceId)
  const history = useHistory()
  const dispatch = useDispatch()

  const handleProductNotFound = () => {
    setOpenConfirmationModal(true)
  }

  const closeConfirmationModal = () => {
    setOpenConfirmationModal(false)
  }

  const onSubmitProductNotFound = () => {
    if (!activeDivergenceId || !user?.userId) {
      toast.error('Dados necessários não encontrados')
      return
    }

    if (isProductRevalidation) {
      if (!activeDivergenceItemId || !user?.badge || !divergenceActiveAddress) {
        toast.error('Dados necessários não encontrados para contagem')
        return
      }

      dispatch(actions.startItemResolution({
        divergenceId: activeDivergenceId,
        divergenceItemId: activeDivergenceItemId,
        userBadge: user.badge,
        locationAddress: divergenceActiveAddress,
        countedQuantity: 0,
        history,
        onSuccess: (result: any) => {
          if (result.isFinished) {
            setCurrentStep(DivergenceFlowStep.FINISHED)
          } else {
            const currentAddress = divergenceActiveAddress
            const currentExpiration = productResolutionData?.expirationDate
            const newAddress = result.data.locationAddress
            const newExpiration = result.data.expirationDate

            if (newAddress !== currentAddress) {
              setIsProductRevalidation(true)
              setTimeout(() => {
                dispatch(actions.setProperty({ property: 'divergenceActiveAddress', value: newAddress }))
              }, 0)
              setCurrentStep(DivergenceFlowStep.ADDRESS)
            } else if (newExpiration !== currentExpiration) {
              setCurrentStep(DivergenceFlowStep.COUNTING)
            } else {
              setCurrentStep(DivergenceFlowStep.COUNTING)
            }
          }
        },
        onError: (error: string) => {
          toast.error(error)
        },
      }))
    } else {
      dispatch(actions.productNotFound({
        divergenceId: activeDivergenceId,
        onSuccess: () => {
          history.push('/restocking/divergence')
        },
        onError: (error: string) => {
          toast.error(error)
        },
      }))
    }

    closeConfirmationModal()
  }

  useEffect(() => {
    dispatch(actions.clearProductResolutionData())

    return () => {
      dispatch(actions.clearProductResolutionData())
    }
  }, [dispatch])
  useEffect(() => {
    if (productResolutionData && currentStep === DivergenceFlowStep.PRODUCT && !isProductRevalidation) {
      setCurrentStep(DivergenceFlowStep.COUNTING)
    }
  }, [productResolutionData, currentStep, isProductRevalidation])

  useEffect(() => {
    if (!divergenceActiveAddress) {
      history.replace('/restocking/divergence')
    }
  }, [divergenceActiveAddress, history])

  const onAddressSubmit = () => {
    const addressCode = getAddressValues('addressCode').trim()
    if (addressCode !== divergenceActiveAddress) {
      setAddressValue('addressCode', '')
      setHasAddressError(true)
      return
    }

    setHasAddressError(false)
    setAddressValue('addressCode', '')
    setCurrentStep(DivergenceFlowStep.PRODUCT)
  }

  const onProductSubmit = () => {
    const barcode = getProductValues('barcode').trim()

    if (isProductRevalidation) {
      dispatch(actions.validateProduct({
        divergenceId: activeDivergenceId!,
        prodBarcode: barcode,
        onSuccess: () => {
          setIsProductRevalidation(false)
          setCurrentStep(DivergenceFlowStep.COUNTING)
        },
        onError: (error: string) => {
          toast.error(error)
        },
      }))
    } else {
      dispatch(actions.startDivergenceResolution({
        divergenceId: activeDivergenceId!,
        prodBarcode: barcode,
      }))
    }

    setProductValue('barcode', '')
  }

  const onCountSubmit = (data: CountFormData) => {
    const quantity = data.quantity.trim()

    if (!activeDivergenceId || !activeDivergenceItemId || !user?.userId || !user?.badge || !divergenceActiveAddress) {
      toast.error('Dados necessários não encontrados')
      return
    }

    dispatch(actions.startItemResolution({
      divergenceId: activeDivergenceId,
      divergenceItemId: activeDivergenceItemId,
      userBadge: user.badge,
      locationAddress: divergenceActiveAddress,
      countedQuantity: Number(quantity),
      history,
      onSuccess: (result: any) => {
        setCountValue('quantity', '')

        if (result.isFinished) {
          setCurrentStep(DivergenceFlowStep.FINISHED)
        } else {
          const currentAddress = divergenceActiveAddress
          const currentExpiration = productResolutionData?.expirationDate
          const newAddress = result.data.locationAddress
          const newExpiration = result.data.expirationDate

          if (newAddress !== currentAddress) {
            setIsProductRevalidation(true)
            setTimeout(() => {
              dispatch(actions.setProperty({ property: 'divergenceActiveAddress', value: newAddress }))
            }, 0)
            setCurrentStep(DivergenceFlowStep.ADDRESS)
          } else if (newExpiration !== currentExpiration) {
            setCurrentStep(DivergenceFlowStep.COUNTING)
          }
        }
      },
      onError: (error: string) => {
        toast.error(error)
      },
    }))
  }

  const formatExpirationDate = (dateString: string) => {
    if (!dateString) return 'Data não informada'

    try {
      const [year, month, day] = dateString.split('-').map(Number)
      const date = new Date(year, month - 1, day)

      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR')
      }

      return 'Data inválida'
    } catch (error) {
      return 'Erro ao formatar data'
    }
  }

  const renderAddressStep = () => (
    <AddressStep
      divergenceActiveAddress={divergenceActiveAddress}
      onAddressSubmit={onAddressSubmit}
      registerAddress={registerAddress}
      hasAddressError={hasAddressError}
    />
  )

  const renderProductStep = () => (
    <ProductStep
      divergenceActiveAddress={divergenceActiveAddress}
      divergenceActiveProductDesc={divergenceActiveProductDesc}
      onProductSubmit={onProductSubmit}
      registerProduct={registerProduct}
      handleProductNotFound={handleProductNotFound}
    />
  )

  const renderCountingStep = () => (
    <CountingStep
      divergenceActiveAddress={divergenceActiveAddress}
      divergenceActiveProductDesc={divergenceActiveProductDesc}
      productResolutionData={productResolutionData}
      onCountSubmit={handleCountSubmit(onCountSubmit)}
      registerCount={registerCount}
      formatExpirationDate={formatExpirationDate}
      errors={countErrors}
    />
  )

  const renderFinishedStep = () => (
    <FinishedStep onFinish={() => history.push('/restocking/divergence')} />
  )

  const renderCurrentStep = () => {
    switch (currentStep) {
      case DivergenceFlowStep.ADDRESS:
        return renderAddressStep()
      case DivergenceFlowStep.PRODUCT:
        return renderProductStep()
      case DivergenceFlowStep.COUNTING:
        return renderCountingStep()
      case DivergenceFlowStep.FINISHED:
        return renderFinishedStep()
      default:
        return renderAddressStep()
    }
  }

  return (
    <Page headerTitle='Divergência' goBackRoute='/restocking/divergence' hasBackButton>
      {renderCurrentStep()}
      <ConfirmOrRejectModal
        show={openConfirmationModal}
        title='Confirmação'
        description='Deseja confirmar que não encontrou o produto?'
        onConfirm={onSubmitProductNotFound}
        onReject={closeConfirmationModal}
        onClose={closeConfirmationModal}
        dataTestId='modal-confirmation-not-found-product-test-id'
      />
    </Page>
  )
}

export default DivergenceProductPage
